import React, { useCallback } from 'react'
import { PerformanceIndicatorType } from 'scenes/Dashboard/data/performanceIndicators/types'
import { getFirstMonthDate, getFormFormattedDate } from 'utils'
import { insertItem, removeItemByIndex } from 'utils-next/arrays'
import { connect, FormProps } from 'utils-next/forms'

import { Typography } from '@mui/material'

import AddItemButton from '../../../../../AddItemButton'
import { OrganizationFormValues } from '../../OrganizationDetailsEdit.models'
import { useStyles } from '../FormSection.styles'
import PerformanceIndicatorEdit from './PerformanceIndicatorEdit.component'
import { getOptionFromPerformanceIndicatorType } from './PerformanceIndicatorsEditList.models'

type PerformanceIndicatorEditListProps = { formik: FormProps<OrganizationFormValues> }

const PerformanceIndicatorEditList = React.memo(
  ({
    formik: {
      setFieldValue,
      values: { performanceIndicatorsValues },
    },
  }: PerformanceIndicatorEditListProps) => {
    const { classes } = useStyles()

    const onAddPerformanceIndicatorClick = useCallback(
      () =>
        setFieldValue(
          'performanceIndicatorsValues',
          insertItem(
            performanceIndicatorsValues,
            buildNumericPerformanceIndicator(PerformanceIndicatorType.monthlyRevenue)
          )
        ),
      [performanceIndicatorsValues, setFieldValue]
    )

    const handleDelete = useCallback(
      (index: number) =>
        setFieldValue('performanceIndicatorsValues', removeItemByIndex(performanceIndicatorsValues, index)),
      [performanceIndicatorsValues, setFieldValue]
    )

    return (
      <div className={classes.sectionContainer}>
        <Typography variant="h6">Performance Indicators</Typography>
        {performanceIndicatorsValues.map((performanceIndicatorValue, index) => (
          <PerformanceIndicatorEdit
            key={performanceIndicatorValue.id}
            className={classes.sectionItem}
            index={index}
            name="performanceIndicatorsValues"
            performanceIndicatorValue={performanceIndicatorValue}
            onClickDelete={() => handleDelete(index)}
          />
        ))}
        <AddItemButton label="Add Performance Indicator" onClick={onAddPerformanceIndicatorClick}>
          Add Performance Indicator
        </AddItemButton>
      </div>
    )
  }
)

PerformanceIndicatorEditList.displayName = 'PerformanceIndicatorList'

export const buildNumericPerformanceIndicator = (type: string | null = null) => ({
  id: undefined,
  value: null,
  type: type ? getOptionFromPerformanceIndicatorType(type) : null,
  measuredOn: getFormFormattedDate(getFirstMonthDate()),
})

export const buildRangePerformanceIndicator = (type: string | null = null) => ({
  id: undefined,
  value: { min: null, max: null },
  type: type ? getOptionFromPerformanceIndicatorType(type) : null,
  measuredOn: getFormFormattedDate(getFirstMonthDate()),
})

export default connect<Record<string, unknown>, OrganizationFormValues>(PerformanceIndicatorEditList)
